/**
 * ReadinessBadge — small percentage pill for pipeline cards.
 *
 * Rendered inside `<KanbanCard>` in `GrantPipeline.jsx`. Data comes
 * from ONE batch call `GET /api/grants/pipeline/readiness` made by
 * the parent page (no N+1 fetches).
 *
 * Threshold-colored status dot + percentage. When
 * `readiness_percentage` is null (grant has no required docs), the
 * badge renders as a compact "—" indicator so the card visual stays
 * consistent without misleading with a "100%" claim.
 */
import { ClipboardCheck } from "lucide-react";

function dotColor(pct) {
  if (pct === null || pct === undefined) return "#94A3B8"; // grey
  if (pct >= 100) return "#10B981";
  if (pct >= 80) return "#84CC16";
  if (pct >= 50) return "#FBBF24";
  return "#EF4444";
}

export default function ReadinessBadge({ summary, testId }) {
  const pct = summary?.readiness_percentage;
  const color = dotColor(pct);
  const label = pct === null || pct === undefined ? "—" : `${pct}%`;

  return (
    <div
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{
        background: "rgba(148,163,184,0.10)",
        border: "1px solid var(--v2-border)",
        color: "var(--v2-textMuted)",
      }}
      title={
        pct === null || pct === undefined
          ? "This grant does not list specific document requirements"
          : `${summary?.present_count ?? 0} on file · ${summary?.warning_count ?? 0} to review · ${summary?.missing_count ?? 0} to upload`
      }
    >
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 6,
          height: 6,
          borderRadius: 999,
          background: color,
        }}
      />
      <ClipboardCheck className="w-2.5 h-2.5" aria-hidden />
      <span data-testid={`${testId}-value`}>{label}</span>
    </div>
  );
}
